/**
 * payDataApi — RTK Query slice cho /purchase/pay-data/* (Procurement Pay Data).
 * Tag types: 'PayData' (list) + 'PayDataDetail' (single).
 */
import { baseApi } from './baseApi'
import type {
  DataResponse,
  MessageResponse,
  PageResponse,
  PayDataDetail,
  PayDataLookupResponse,
  PayDataListItem,
  PayDataListQuery,
  PayDataPrintResult,
  PayDataWrite,
} from '@/types/api'

export const payDataApi = baseApi.injectEndpoints({
  endpoints: (b) => ({
    listPayData: b.query<PageResponse<PayDataListItem>, PayDataListQuery | void>({
      query: (params) => ({ url: '/purchase/pay-data/list', params: params ?? {} }),
      providesTags: (res) =>
        res
          ? [
              ...res.items.map((it) => ({ type: 'PayData' as const, id: it.paydata_p_id })),
              { type: 'PayData' as const, id: 'LIST' },
            ]
          : [{ type: 'PayData' as const, id: 'LIST' }],
    }),
    getPayData: b.query<DataResponse<PayDataDetail>, number>({
      query: (id) => `/purchase/pay-data/${id}`,
      providesTags: (_r, _e, id) => [{ type: 'PayDataDetail', id }],
    }),
    createPayData: b.mutation<DataResponse<PayDataDetail>, PayDataWrite>({
      query: (body) => ({ url: '/purchase/pay-data/create', method: 'POST', body }),
      invalidatesTags: [{ type: 'PayData', id: 'LIST' }],
    }),
    updatePayData: b.mutation<DataResponse<PayDataDetail>, { id: number; body: PayDataWrite }>({
      query: ({ id, body }) => ({ url: `/purchase/pay-data/${id}`, method: 'PUT', body }),
      invalidatesTags: (_r, _e, arg) => [
        { type: 'PayData', id: arg.id },
        { type: 'PayData', id: 'LIST' },
        { type: 'PayDataDetail', id: arg.id },
      ],
    }),
    deletePayData: b.mutation<MessageResponse, number>({
      query: (id) => ({ url: `/purchase/pay-data/${id}`, method: 'DELETE' }),
      invalidatesTags: (_r, _e, id) => [
        { type: 'PayData', id },
        { type: 'PayData', id: 'LIST' },
      ],
    }),
    /** Multi-delete (ticked rows). */
    deleteManyPayData: b.mutation<MessageResponse, number[]>({
      query: (ids) => ({ url: '/purchase/pay-data/delete-many', method: 'POST', body: ids }),
      invalidatesTags: [{ type: 'PayData', id: 'LIST' }],
    }),
    /** access-suggest: next xntt_no theo contract. */
    suggestXnttNo: b.query<{ suggestedNo: string }, { contractNo: string }>({
      query: ({ contractNo }) =>
        `/purchase/pay-data/suggest/xntt?contractNo=${encodeURIComponent(contractNo)}`,
      transformResponse: (r: { suggested_no?: string }) => ({ suggestedNo: r.suggested_no ?? '' }),
      keepUnusedDataFor: 0,
    }),
    checkXnttNoExists: b.query<{ exists: boolean }, { xnttNo: string }>({
      query: ({ xnttNo }) => `/purchase/pay-data/check/xntt?xnttNo=${encodeURIComponent(xnttNo)}`,
      keepUnusedDataFor: 0,
    }),
    // Lookups
    payDataPaymentTypeLookup: b.query<PayDataLookupResponse, void>({
      query: () => '/purchase/pay-data/lookups/payment-type',
    }),
    payDataBankLookup: b.query<PayDataLookupResponse, { cpCode?: string } | void>({
      query: (arg) => ({
        url: '/purchase/pay-data/lookups/bank',
        params: arg && arg.cpCode ? { cp_code: arg.cpCode } : {},
      }),
    }),
    payDataBankAccountLookup: b.query<PayDataLookupResponse, { cpCode?: string; bank?: string } | void>({
      query: (arg) => ({
        url: '/purchase/pay-data/lookups/bank-account',
        params: {
          ...(arg && arg.cpCode ? { cp_code: arg.cpCode } : {}),
          ...(arg && arg.bank ? { bank: arg.bank } : {}),
        },
      }),
    }),
    payDataTruckIdLookup: b.query<PayDataLookupResponse, { contractNo?: string } | void>({
      query: (arg) => ({
        url: '/purchase/pay-data/lookups/truck-id',
        params: arg && arg.contractNo ? { contract_no: arg.contractNo } : {},
      }),
    }),
    payDataInvoiceNoLookup: b.query<
      PayDataLookupResponse,
      { contractNo?: string; contractNo2?: string } | void
    >({
      query: (arg) => ({
        url: '/purchase/pay-data/lookups/invoice-no',
        params: {
          ...(arg && arg.contractNo ? { contract_no: arg.contractNo } : {}),
          ...(arg && arg.contractNo2 ? { contract_no2: arg.contractNo2 } : {}),
        },
      }),
    }),
    payDataContractsLookup: b.query<PayDataLookupResponse, { kind?: 'p' | 'npe'; q?: string } | void>({
      query: (arg) => ({
        url: '/purchase/pay-data/lookups/contracts',
        params: {
          ...(arg && arg.kind ? { kind: arg.kind } : {}),
          ...(arg && arg.q ? { q: arg.q } : {}),
        },
      }),
    }),
    payDataContractInfo: b.query<Record<string, unknown>, { contractNo: string }>({
      query: ({ contractNo }) =>
        `/purchase/pay-data/contract-info?contractNo=${encodeURIComponent(contractNo)}`,
      keepUnusedDataFor: 0,
    }),
    /** Invoice snapshot (invoice_date / amount / vat) theo invoice_no — mirror
     *  DLookup Access của form Add (Invoice Information RO fields). */
    payDataInvoiceInfo: b.query<Record<string, unknown>, { invoiceNo: string }>({
      query: ({ invoiceNo }) =>
        `/purchase/pay-data/lookups/invoice-info?invoiceNo=${encodeURIComponent(invoiceNo)}`,
      keepUnusedDataFor: 0,
    }),
    /** Price units + x_to_mt — Check_Amount quy đổi USD/VND (Access public_price_unit). */
    payDataPriceUnitsLookup: b.query<{ price_unit: string; x_to_mt: number | null }[], void>({
      query: () => '/purchase/pay-data/lookups/price-units',
    }),
    /** Provisional snapshot theo contract+truck (Access Check_Amount →
     *  v_paydata_p_checkprovisional auto-fill futures/fx/price/price_unit). */
    payDataProvisionalCheck: b.query<
      Record<string, unknown>,
      { contractNo: string; truckId: string }
    >({
      query: ({ contractNo, truckId }) =>
        `/purchase/pay-data/lookups/provisional-check?contractNo=${encodeURIComponent(contractNo)}&truckId=${encodeURIComponent(truckId)}`,
      keepUnusedDataFor: 0,
    }),
    /** Pay-Confirm print preview (BE chọn variant theo row). */
    printPayConfirm: b.query<DataResponse<PayDataPrintResult>, number>({
      query: (id) => `/purchase/pay-data/${id}/print`,
    }),
  }),
})

export const {
  useListPayDataQuery,
  useGetPayDataQuery,
  useLazyGetPayDataQuery,
  useCreatePayDataMutation,
  useUpdatePayDataMutation,
  useDeletePayDataMutation,
  useDeleteManyPayDataMutation,
  useLazySuggestXnttNoQuery,
  useLazyCheckXnttNoExistsQuery,
  usePayDataPaymentTypeLookupQuery,
  usePayDataBankLookupQuery,
  useLazyPayDataBankLookupQuery,
  useLazyPayDataBankAccountLookupQuery,
  useLazyPayDataTruckIdLookupQuery,
  useLazyPayDataInvoiceNoLookupQuery,
  useLazyPayDataContractsLookupQuery,
  usePayDataContractsLookupQuery,
  useLazyPayDataContractInfoQuery,
  useLazyPayDataInvoiceInfoQuery,
  usePayDataPriceUnitsLookupQuery,
  useLazyPayDataProvisionalCheckQuery,
  useLazyPrintPayConfirmQuery,
} = payDataApi
